import React, { useState, useEffect, useCallback } from 'react'
import { styled } from '@mui/system'
import { Button, Box, Chip } from '@mui/material'
import GitHubIcon from '@mui/icons-material/GitHub'
import EditIcon from '@mui/icons-material/Edit'
import { useIntl } from 'react-intl'
import { CellPropsBase } from '.'
import TextVO from '../../../../../../vo/TextVO'
import { ValidationError } from '../../../../meta/validator'
import GitHubUserNameRegistrationDialog from '../../../../../components/dialogs/GitHubUserNameRegistrationDialog'
import store from '../../../../../../store'
import { requireSave } from '../../../../../../store/requiredSaveData'

const GitHubContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: '24px',
  padding: '4px 8px',
})

const UsernameChip = styled(Chip)({
  height: '24px',
  fontSize: '12px',
})

const RegisterButton = styled(Button)({
  fontSize: '12px',
  padding: '2px 8px',
  minHeight: '24px',
})

const GitHubUserNameCell: React.FC<CellPropsBase<TextVO>> = props => {
  const intl = useIntl()
  // より安全な初期値の取得
  const getInitialValue = useCallback((value: any): string => {
    if (!value) return ''
    if (typeof value === 'string') return value
    if (
      typeof value === 'object' &&
      value !== null &&
      'getValue' in value &&
      typeof value.getValue === 'function'
    ) {
      return value.getValue() || ''
    }
    return ''
  }, [])

  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false)
  const [currentUsername, setCurrentUsername] = useState<string>(
    getInitialValue(props.value)
  )

  // propsの変更に対応
  useEffect(() => {
    const newUsername = getInitialValue(props.value)

    // valueが定義されている場合のみ更新する
    if (props.value !== undefined) {
      setCurrentUsername(newUsername)
    }
  }, [props.value])

  const handleCloseRegistration = useCallback(() => {
    setShowRegistrationDialog(false)
  }, [])

  const handleSaveGitHubUserName = useCallback(
    (username: string) => {
      // TextVOを作成
      const newTextVO = new TextVO(username)

      // ステートを更新
      setCurrentUsername(username)

      // 親のonChangeを呼び出してDataTableのonCellChangeに伝達
      props.onChange(newTextVO)

      // ダイアログを閉じる
      setShowRegistrationDialog(false)
    },
    [props.onChange]
  )

  const handleEdit = useCallback(() => {
    setShowRegistrationDialog(true)
  }, [])

  const handleRegister = useCallback(() => {
    setShowRegistrationDialog(true)
  }, [])

  const { cellDef, value } = props

  // より安全な値の取得
  let githubUserName: string | null = null
  if (currentUsername) {
    githubUserName = currentUsername
  } else if (value) {
    // valueがTextVOオブジェクトの場合
    if (
      typeof value === 'object' &&
      value !== null &&
      'getValue' in value &&
      typeof value.getValue === 'function'
    ) {
      githubUserName = value.getValue()
    }
    // valueが文字列の場合
    else if (typeof value === 'string') {
      githubUserName = value
    }
  }

  const editable =
    typeof cellDef?.editable === 'function'
      ? cellDef.editable(props.data)
      : cellDef?.editable

  return (
    <GitHubContainer>
      {githubUserName ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <UsernameChip
            icon={<GitHubIcon sx={{ fontSize: 16 }} />}
            label={githubUserName}
            variant="outlined"
            size="small"
          />
          {editable && (
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={handleEdit}
              sx={{
                minWidth: 'auto',
                padding: '2px 4px',
                fontSize: '11px',
              }}
            >
              {intl.formatMessage({ id: 'edit' })}
            </Button>
          )}
        </Box>
      ) : (
        editable && (
          <RegisterButton
            variant="outlined"
            size="small"
            startIcon={<GitHubIcon sx={{ fontSize: 16 }} />}
            onClick={handleRegister}
          >
            {intl.formatMessage({ id: 'github.register.button' })}
          </RegisterButton>
        )
      )}

      {/* GitHubユーザー名登録ダイアログ */}
      {showRegistrationDialog && (
        <GitHubUserNameRegistrationDialog
          open={true}
          onClose={handleCloseRegistration}
          onSave={handleSaveGitHubUserName}
          currentUsername={currentUsername || githubUserName || ''}
        />
      )}
    </GitHubContainer>
  )
}

export default GitHubUserNameCell
